import { flatten } from "ramda";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError, updateSyncWarnings } from "redux-form";
import { CalloutIntent, HStack, InfoToggletip } from "widgets";

import { type ICreateGroupParams } from "@/api/WebApiClient.ts";
import { getDisabledPrjCapabilityTooltipMessage, prjPrivateSavingAllowed } from "@/app-utils/capability.ts";
import { isSaveableType } from "@/app-utils/entity.ts";
import { simpleDropdownFieldValidation, simpleFieldValidation } from "@/app-utils/form.ts";
import { mapSequenceTypeToEntityType } from "@/app-utils/route.ts";
import { encodeFilter, type IRsqlComparisonExpr, SEARCH_OPERATORS } from "@/app-utils/rsql.ts";
import {
  type Group,
  type InputTableQuerySpec,
  type Project,
  type SequenceFeatureTypes,
  type UserGroupItemValidationResult,
  type View,
} from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { type IJobState } from "@/redux/modules/job.ts";
import {
  getSequenceGroupTypes,
  saveSelectedRows,
  validateSingleGroupAgainstProjectUserGroup,
} from "@/redux/modules/saved-group.ts";
import {
  savedViewAppliesToTableQuerySpec,
  type SavedViewState,
  saveView,
  type SaveViewParams,
  validateSingleViewAgainstProjectUserGroup,
} from "@/redux/modules/saved-view.ts";
import {
  filterByTableColumn,
  getTableDataKey,
  getTableQuerySpecWithSelection,
  type IFilterPayload,
  type IIdentifiable,
  type ITableData,
} from "@/redux/modules/table-data.ts";
import { findUserGroupColumnFieldFromTableData } from "@/redux/modules/user-group-admin.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY, ENTITY_TYPE, MAX_LENGTH, VIEW } from "@/settings/settings.ts";
import { ProjectTagInputReduxFormField } from "@/ui-modules/core/redux-form/ProjectTagInputField.tsx";
import {
  createSavableEntityInputParams,
  FORM_GROUP,
  formatSourceOptionLabel,
  getDefaultSourceOption,
  getSaveAsOptionParameters,
  getSourceOptions,
  getTypeName,
  GROUP_ENTITY,
  type ISavableEntityParams,
  SaveDialogInputFields,
  SelectionType,
  type SourceSelectOption,
  type TargetEntityOption,
  VIEW_ENTITY,
} from "@/ui-modules/core/save-dialog/utils.ts";

import ProjectsSelect from "../forms/ProjectsSelect.js";
import { Dialog, type IPopoverProps } from "../overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../redux-form/ReactSelectField.js";

export interface SaveDialogProps {
  // props from the parent entity
  canSaveView?: boolean;
  canSaveGroup?: boolean;

  viewId?: string;
  viewType?: string;
  subViewType?: string;

  // Saving groups requires entityType or activeTables
  activeTables?: ITableData[];
  entityType?: string;
  // a single entity (eg: IGene) ONLY for Summary Page case. We use only the id
  entity?: IIdentifiable;

  close?: () => void;
}

const SaveDialogImpl: FC<SaveDialogProps & InjectedFormProps<never, never>> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const selectedSource = useSelector<IAppState, SourceSelectOption>((state) =>
    formValueSelector(FORM_GROUP)(state, SaveDialogInputFields.FIELD_SOURCE),
  );
  const selectedProject = useSelector<IAppState, Project>((state) =>
    formValueSelector(FORM_GROUP)(state, SaveDialogInputFields.FIELD_DESTINATION),
  );
  const isUpdateOptionSelected = useSelector<IAppState, boolean>((state) =>
    formValueSelector(FORM_GROUP)(state, SaveDialogInputFields.FIELD_UPDATE),
  );
  // @ts-ignore
  const tables: ITableData[] = useSelector<IAppState>((state) => {
    return props.activeTables && props.activeTables.length > 0
      ? props.activeTables
      : state.tableData.activeTable
        ? [state.tableData.tables[getTableDataKey(state.tableData.activeTable)]]
        : null;
  });
  const jobState = useSelector<IAppState, IJobState>((state) => state.job);
  const isGenomeBrowserRangeSelected = useSelector<IAppState, boolean>((state) => !!state.genomeBrowser.selectedRange);
  const maxSelectedTableRows = useSelector<IAppState, number | undefined>(
    (state) => state.config.configuration?.application?.maxSelectedTableRows ?? undefined,
  );
  const sequenceFeatureTypes = useSelector<IAppState, SequenceFeatureTypes>(
    (state) => state.listing.sequenceFeatureTypes,
  );
  const { view } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const formState = useSelector<IAppState, FormData>((state) => state.form[FORM_GROUP]);

  const isPrivateWorkspace = selectedProject?.isPrivateWorkspace;
  const privateSavingAllowed = selectedProject && prjPrivateSavingAllowed(selectedProject);

  const [sourceOptions, setSourceOptions] = useState([]);
  const [isLoadingSourceOptions, setIsLoadingSourceOptions] = useState(false);

  const previousState = useRef<FormData | undefined>(undefined);

  const viewUpdateAllowed = useMemo(() => {
    if (view && view.deletable) {
      if (selectedSource?.selectionType === SelectionType.CurrentView) {
        if (view.viewType === VIEW.TABLE_VIEW.type) {
          return savedViewAppliesToTableQuerySpec(view, props.activeTables?.[0]?.tableQuerySpec);
        }
        return view.viewType === props.viewType; // view type should be the same (e.g. you can switch to a different summary page)
      }
    }
    return false;
  }, [view, selectedSource]);

  useEffect(() => {
    if (!props.canSaveGroup && !props.canSaveView) {
      throw new Error("SaveAs: neither canSaveGroup nor canSaveView is set!");
    }

    setIsLoadingSourceOptions(true);
    void createSourceOptions().then((sourceOptions) => {
      const defaultOption = getDefaultSourceOption(
        sourceOptions,
        props.viewType,
        props.entity,
        tables,
        isGenomeBrowserRangeSelected,
      );
      props.change(SaveDialogInputFields.FIELD_SOURCE, defaultOption);
      setIsLoadingSourceOptions(false);
      setSourceOptions(sourceOptions);
    });
  }, []);

  useEffect(() => {
    if (
      previousState?.current?.values[SaveDialogInputFields.FIELD_DESTINATION] == undefined &&
      selectedProject != undefined
    ) {
      setTimeout(() => {
        props.asyncValidate();
      }, 0);
    }
    props.change(SaveDialogInputFields.FIELD_MAKEPRIVATE, isPrivateWorkspace);
    // do not add props to the deps. It causes flickering on the project list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivateWorkspace, selectedProject]);

  // ===============================================================
  // Keep the previously entered name and description if the update
  // options is deselected or if the selectedSource changes (as it was before)
  useEffect(() => {
    if (isUpdateOptionSelected) {
      previousState.current = formState;
      props.change(SaveDialogInputFields.FIELD_NAME, view.name);
      props.change(SaveDialogInputFields.FIELD_DESCRIPTION, view.description);
      props.change(SaveDialogInputFields.FIELD_DESTINATION, view.project);
      props.change(SaveDialogInputFields.FIELD_MAKEPRIVATE, !view.shared);
      props.change(
        SaveDialogInputFields.FIELD_TAGS,
        view.itemTags.map((tag) => tag.id),
      );
    } else {
      props.change(SaveDialogInputFields.FIELD_NAME, previousState.current?.values[SaveDialogInputFields.FIELD_NAME]);
      props.change(
        SaveDialogInputFields.FIELD_MAKEPRIVATE,
        previousState.current?.values[SaveDialogInputFields.FIELD_MAKEPRIVATE],
      );
      props.change(
        SaveDialogInputFields.FIELD_DESCRIPTION,
        previousState.current?.values[SaveDialogInputFields.FIELD_DESCRIPTION],
      );
      props.change(
        SaveDialogInputFields.FIELD_DESTINATION,
        previousState.current?.values[SaveDialogInputFields.FIELD_DESTINATION],
      );
    }
  }, [isUpdateOptionSelected]);

  useEffect(() => {
    if (selectedSource?.selectionType !== SelectionType.CurrentView) {
      props.change(SaveDialogInputFields.FIELD_UPDATE, undefined);
    }
  }, [selectedSource]);

  // ===============================================================

  const createSaveGroupOptions = useCallback(async (table?: ITableData) => {
    if (table?.tableQuerySpec.resultType === ENTITY_TYPE.GENERIC_SEQ) {
      // in case of sequence, we will need to figure out the potential target depending on the selection
      const sequenceGroupTypes = await dispatch(getSequenceGroupTypes(getTableQuerySpecWithSelection(table)));
      const targetEntityOptions: TargetEntityOption[] = sequenceGroupTypes.map((sequenceGroupType) => {
        return {
          targetEntity: mapSequenceTypeToEntityType(sequenceGroupType.sequenceGroup.name),
          targetRowIds: [],
          targetSelectedIds: sequenceGroupType.genericSeqIds,
          multiTypesTarget: sequenceGroupTypes.length > 1,
        };
      });
      return getSourceOptions(targetEntityOptions, table, props.viewType, maxSelectedTableRows);
    }

    const entityType: string = getTypeName(props.entity, props.entityType, table);
    const targetEntityOptions: TargetEntityOption[] = getSaveAsOptionParameters(
      entityType,
      table,
      props.entity,
      jobState,
    );

    return Promise.resolve(getSourceOptions(targetEntityOptions, table, props.viewType, maxSelectedTableRows));
  }, []);

  const createSourceOptions = useCallback(async () => {
    const { canSaveGroup, canSaveView } = props;

    const optionList: SourceSelectOption[] = [];

    const asyncOptionCreationPromises: Promise<SourceSelectOption[]>[] = [];
    if (canSaveGroup) {
      // "Save": Build list of savable data sets (sources).
      if (props.entity) {
        // for Summary View
        asyncOptionCreationPromises.push(createSaveGroupOptions());
      } else if (tables) {
        asyncOptionCreationPromises.push(
          ...tables
            .filter((table: ITableData) => isSaveableType(table.tableQuerySpec.resultType))
            .map((table: ITableData) => createSaveGroupOptions(table)),
        );
      }
    }
    return Promise.all(asyncOptionCreationPromises).then((values) => {
      const allOptions = flatten(values);
      optionList.push(...allOptions);
      if (canSaveView) {
        optionList.push({
          selectionType: SelectionType.CurrentView,
          table: tables?.length ? tables[0] : undefined,
        });
      }
      return optionList;
    });
  }, []);

  function handleSubmitData(values: Partial<FormData>): Promise<View | Group> {
    const sourceSelection: SourceSelectOption = values[SaveDialogInputFields.FIELD_SOURCE];
    const selectionType: SelectionType = sourceSelection.selectionType;
    let mutationFunction = null;
    if (selectionType === SelectionType.CurrentView) {
      mutationFunction = (payload: SaveViewParams) =>
        dispatch(saveView(payload, !!values[SaveDialogInputFields.FIELD_UPDATE]));
    } else if (selectionType === SelectionType.AllRows || selectionType === SelectionType.SelectedRows) {
      mutationFunction = (payload: ICreateGroupParams) => dispatch(saveSelectedRows(payload));
    }

    const payload: SaveViewParams = createSavableEntityInputParams(values, props, view?.id);

    if (mutationFunction == null) {
      throw new SubmissionError({ _error: "selectionType is unknown" });
    }

    return mutationFunction(payload).then(
      (fulfilled: Promise<never>) => {
        props.close && props.close();
        props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  }

  const privateFieldLabel = `Private (Hidden from other ${getProjectLabel(false, false)} members)`;

  const getTooltipTextForIsPrivate = useMemo(() => {
    let toolTipTextForIsPrivate: string = privateFieldLabel;
    if (isPrivateWorkspace) {
      toolTipTextForIsPrivate = "Items in the Private Workspace are never shared with other users.";
    } else if (!privateSavingAllowed) {
      toolTipTextForIsPrivate = getDisabledPrjCapabilityTooltipMessage(CAPABILITY.PRIVATE_SAVING);
    }
    return toolTipTextForIsPrivate;
  }, [isPrivateWorkspace, privateSavingAllowed]);

  const getFooterMessage = useMemo(() => {
    let footerMessage: IPopoverProps["footerMessage"];
    if (props.error) {
      footerMessage = { message: props.error };
    } else if (props.warning) {
      footerMessage = { message: props.warning, intent: CalloutIntent.Warning };
    }
    return footerMessage;
  }, [props.error, props.warning]);

  return (
    <Dialog
      closeFn={props.close}
      mainActionButtonProps={{
        isLoading: !!props.asyncValidating || props.submitting,
        isDisabled: !!props.error,
        children: "Save",
        onPress: props.handleSubmit(handleSubmitData),
        label: undefined,
      }}
      footerMessage={getFooterMessage}
    >
      <ReactSelectReduxFormField
        name={SaveDialogInputFields.FIELD_SOURCE}
        label="Save"
        tooltip="Select the content you would like to save"
        placeholder="Please select..."
        options={sourceOptions}
        getOptionValue={(option: SourceSelectOption) => `${option.selectionType}-${option.targetEntityType}`}
        component={ReactSelectField}
        isClearable={false}
        isSearchable={false}
        validate={simpleDropdownFieldValidation}
        isLoading={isLoadingSourceOptions}
        forceWarnState={selectedSource?.multiTypesTarget ? "Multiple target types are available." : null}
        formatOptionLabel={(data: SourceSelectOption) =>
          formatSourceOptionLabel(data, props.viewType, sequenceFeatureTypes, maxSelectedTableRows)
        }
      />
      {viewUpdateAllowed && (
        <CheckboxReduxFormField
          name={SaveDialogInputFields.FIELD_UPDATE}
          label={
            <HStack>
              {"Update current view"}
              <InfoToggletip
                content={"The previously saved view will be overwritten. Project and visibility are preserved."}
              />
            </HStack>
          }
          component={CheckboxField}
          style={{ marginTop: 0 }}
          info={isUpdateOptionSelected && "The previously saved view will be overwritten."}
        />
      )}

      <ProjectsSelect
        name={SaveDialogInputFields.FIELD_DESTINATION}
        title={"Save to"}
        tooltip="Select a destination"
        onInitToLastTarget={props.change}
        isDisabled={isUpdateOptionSelected}
        onChange={() => props.change(SaveDialogInputFields.FIELD_TAGS, undefined)}
      />

      <CheckboxReduxFormField
        name={SaveDialogInputFields.FIELD_MAKEPRIVATE}
        label={privateFieldLabel}
        tooltipText={getTooltipTextForIsPrivate}
        disabled={isPrivateWorkspace || !privateSavingAllowed}
        component={CheckboxField}
        style={{ marginTop: 0 }}
      />

      <InputReduxFormField
        componentClass="input"
        name={SaveDialogInputFields.FIELD_NAME}
        label="Name"
        placeholder={`Enter ${
          selectedSource?.selectionType === SelectionType.CurrentView ? VIEW_ENTITY : GROUP_ENTITY
        } name`}
        maxLength={MAX_LENGTH.GROUP.NAME}
        component={InputField}
        validate={simpleFieldValidation}
      />

      <InputReduxFormField
        componentClass="textarea"
        name={SaveDialogInputFields.FIELD_DESCRIPTION}
        label="Description"
        placeholder="Optional description"
        maxLength={MAX_LENGTH.GROUP.DESCRIPTION}
        component={InputField}
      />

      <ProjectTagInputReduxFormField
        name={SaveDialogInputFields.FIELD_TAGS}
        label="Tags"
        projectId={selectedProject?.id}
      />
    </Dialog>
  );
};

const asyncValidate = async (values: Partial<FormData>, dispatch: ReduxDispatch, props: SaveDialogProps) => {
  const source: SourceSelectOption = values?.[SaveDialogInputFields.FIELD_SOURCE];
  const selectedProject: Project = values?.[SaveDialogInputFields.FIELD_DESTINATION];
  const name: string = values?.[SaveDialogInputFields.FIELD_NAME];

  if (!source || !selectedProject) {
    return;
  }

  try {
    // reset the warning
    dispatch(updateSyncWarnings(FORM_GROUP, undefined, undefined));

    const selectedSelectionType: SelectionType = source.selectionType;
    const selectedProjectId: number = selectedProject?.id;

    // apply the validation of user groups against the list of items to be saved
    if (selectedSelectionType === SelectionType.SelectedRows || selectedSelectionType === SelectionType.AllRows) {
      const type: string = getTypeName(props.entity, props.entityType, source.table);
      const inputSelectionSpec: InputTableQuerySpec = {
        resultType: type,
        restrictions: {
          selection: {
            selectedResultIds: source.targetRowIds as number[],
          },
        },
      };
      const targetType: string = source.targetEntityType ?? type;

      const validationResults: UserGroupItemValidationResult = await dispatch(
        validateSingleGroupAgainstProjectUserGroup(selectedProjectId, type, inputSelectionSpec, targetType),
      );

      // if the number of items that match the target user group is between 0 and the total count then we show a warning
      if (
        validationResults.visibleItemCount > 0 &&
        validationResults.totalItemCount > validationResults.visibleItemCount
      ) {
        // we should construct the filter by user group to offer the user a simple way to see only items
        //   that correspond to the target user group
        const userGroupColumnField: string = findUserGroupColumnFieldFromTableData(source.table);
        const userGroupFilterComparisonExpr: IRsqlComparisonExpr = {
          key: userGroupColumnField,
          operator: SEARCH_OPERATORS.IN,
          value: [validationResults.userGroup ?? ""],
        };
        const userGroupFilterPayload: IFilterPayload = {
          columnField: userGroupColumnField,
          tableSpec: source.table?.tableQuerySpec ?? {},
          filterExpression: encodeFilter(userGroupFilterComparisonExpr),
        };

        const filterLink = (
          <a
            onClick={() => {
              dispatch(filterByTableColumn(userGroupFilterPayload));
              props.close?.();
            }}
          >
            filter the current table
          </a>
        );

        const hiddenItemCount: number = validationResults.totalItemCount - validationResults.visibleItemCount;

        dispatch(
          updateSyncWarnings(
            FORM_GROUP,
            undefined,
            <span>
              {hiddenItemCount} of {validationResults.totalItemCount} items in this group cannot be saved to &apos;
              {selectedProject.name}&apos; due to the user group restrictions. To save the complete group, select a
              different {getProjectLabel(false, false)} or {filterLink} by the {getProjectLabel(false, false)}
              &apos;s user groups.
            </span>,
          ),
        );
      }

      if (validationResults.visibleItemCount === 0) {
        throw new Error(
          `This group cannot be saved to '${
            selectedProject.name
          }' due to the user groups  Please select a different ${getProjectLabel(false, false)}.`,
        );
      }
    } else if (selectedSelectionType === SelectionType.CurrentView) {
      const payload: ISavableEntityParams = createSavableEntityInputParams(values, props);
      payload.name = name || ""; //  this field "name" is not used in the validation process but is returned as part of the error message (if any)
      const valid: boolean = await dispatch(validateSingleViewAgainstProjectUserGroup(payload as SaveViewParams));

      if (!valid) {
        throw new Error(
          `Information contained in this view cannot be saved to '${
            selectedProject.name
          }' due to the user groups  Please select a different ${getProjectLabel(false, false)}.`,
        );
      }
    }
  } catch (error) {
    throw { _error: error.message };
  }
};

const SaveDialog = reduxForm<{}, any>({
  form: FORM_GROUP,
  asyncValidate: asyncValidate,
  asyncChangeFields: [SaveDialogInputFields.FIELD_DESTINATION, SaveDialogInputFields.FIELD_SOURCE],
})(SaveDialogImpl) as any;

export { SaveDialog };
